import {Actions, closeCurrentDialog, openDeleteDialog, openDialog} from 'platform/components';
import {HStack, Heading, Show, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {always, mergeAll} from 'ramda';
import {isArray} from 'ramda-adjunct';

import {useDeleteServicePackageItemsMutation} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {DmsDataGrid, handleApiError, refreshDmsDatagrid} from '@dms/shared';

import {TestIdProps, suffixTestId, useToggle} from 'shared/web';

import type {ActionCallback, QueryFilterObject} from 'features/eag-grid';

import {AddMaterialItemsDialog} from './AddMaterialItemsDialog';
import {AddWorkItemsDialog} from './AddWorkItemsDialog';
import {EditMaterialItem} from './EditMaterialItem';
import {EditWorkItem} from './EditWorkItem';

type CastedRowData = {
  itemType?: {value?: {key?: 'SVCITEMTYPE_PART' | 'SVCITEMTYPE_WORK'}};
  itemName?: {value?: string};
};

interface RequestItemsProps extends TestIdProps {
  servicePackageId: string;
}

const PREDEFINED_COLUMNS = ['itemType', 'itemName'] as const;

export function RequestItems(props: RequestItemsProps) {
  const [isAddWorkOpen, toggleWorkOpen] = useToggle(false);
  const [isAddMaterialOpen, toggleMaterialOpen] = useToggle(false);

  const [deleteServicePackageItems] = useDeleteServicePackageItemsMutation();

  const queryModifier = (filter: QueryFilterObject) =>
    mergeAll([filter, {servicePackageId: props.servicePackageId}]);

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = ({
    actionKey,
    rowId,
    refreshData,
    getColumnData,
  }) => {
    const servicePackageItemId = isArray(rowId) ? rowId[0] : rowId;
    const servicePackageItemIds = isArray(rowId) ? rowId : [rowId];

    const type = getColumnData<{key: string}>('itemType')?.value?.key;
    const itemName = getColumnData<string>('itemName')?.value;

    match(actionKey)
      .with('edit', 'redirectDetail', () =>
        openDialog(
          type === 'SVCITEMTYPE_WORK' ? (
            <EditWorkItem
              context="request"
              itemId={servicePackageItemId}
              servicePackageId={props.servicePackageId}
              onClose={closeCurrentDialog}
              onAfterSubmit={refreshData}
            />
          ) : (
            <EditMaterialItem
              context="request"
              itemId={servicePackageItemId}
              servicePackageId={props.servicePackageId}
              onClose={closeCurrentDialog}
              onAfterSubmit={refreshData}
            />
          ),
          {
            title: itemName || i18n.t('general.labels.edit'),
          }
        )
      )
      .with('delete', () =>
        openDeleteDialog({
          onConfirm: () =>
            deleteServicePackageItems({
              servicePackageId: props.servicePackageId,
              body: {servicePackageItemIds},
            })
              .unwrap()
              .then(() => refreshData())
              .catch(handleApiError),
        })
      )
      .otherwise(always(undefined));
  };

  return (
    <VStack spacing={4}>
      <HStack justify="space-between" align="center">
        <Heading size={4}>{i18n.t('entity.workshopServicePackages.labels.workItems')}</Heading>
        <Actions
          data-testid={suffixTestId('actions', props)}
          actions={[
            {
              type: 'button',
              variant: 'link',
              leftIcon: 'content/add_circle',
              title: i18n.t('entity.workshopServicePackages.actions.addWork'),
              onClick: toggleWorkOpen,
            },
            {
              type: 'button',
              variant: 'link',
              leftIcon: 'content/add_circle',
              title: i18n.t('entity.workshopServicePackages.actions.addMaterial'),
              onClick: toggleMaterialOpen,
            },
          ]}
        />
      </HStack>
      <DmsDataGrid
        gridCode="service-package-items"
        actionCallback={actionCallback}
        predefinedColumns={PREDEFINED_COLUMNS}
        queryModifier={queryModifier}
        data-testid={suffixTestId('dataGrid', props)}
        autoHeight
      />
      <Show when={isAddWorkOpen}>
        <AddWorkItemsDialog
          packageId={props.servicePackageId}
          onClose={toggleWorkOpen}
          onAfterSubmit={() => refreshDmsDatagrid('service-package-items')}
        />
      </Show>
      <Show when={isAddMaterialOpen}>
        <AddMaterialItemsDialog
          packageId={props.servicePackageId}
          onClose={toggleMaterialOpen}
          onAfterSubmit={() => refreshDmsDatagrid('service-package-items')}
        />
      </Show>
    </VStack>
  );
}
